<template>
  <div class="snapshot-filters">
    <DatesFilter v-model="datesValue" />
    <NameFilter v-model="nameValue" label="Snapshot Name" />
    <UserFilter
      v-model="usernameValue"
      :users-list="normalizedUsersList"
    />
    <StatusFilter
      v-model="statusValue"
      :status-list="normalizedStatusList"
    />
    <StatusFilter
      v-model="extensionValue"
      :status-list="normalizedExtensionsList"
      label="File Extension"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import DatesFilter from '@/components/Apps/TaskFilters/DatesFilter.vue';
import NameFilter from '@/components/Apps/TaskFilters/NameFilter.vue';
import UserFilter from '@/components/Apps/TaskFilters/UserFilter.vue';
import StatusFilter from '@/components/Apps/TaskFilters/StatusFilter.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      startDate: '',
      endDate: '',
      dynamicFilters: {},
    }),
  },
  usersList: {
    type: Array,
    default: () => [],
  },
  statusList: {
    type: Array,
    default: () => [],
  },
  extensionsList: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const normalizeOptions = (options = []) => options.map((option) => {
  if (typeof option === 'string') {
    return { key: option, value: option };
  }
  return {
    key: option.key ?? option.label ?? option.value,
    value: option.value ?? option.key ?? option.label,
  };
});

const normalizedUsersList = computed(() => normalizeOptions(props.usersList));
const normalizedStatusList = computed(() => normalizeOptions(props.statusList));
const normalizedExtensionsList = computed(() => normalizeOptions(props.extensionsList));

const emitUpdate = (patch) => {
  const current = props.modelValue || {};
  emit('update:modelValue', {
    ...current,
    ...patch,
    dynamicFilters: { ...(current.dynamicFilters || {}), ...(patch.dynamicFilters || {}) },
  });
};

const emitDynamic = (key, value) => {
  emitUpdate({ dynamicFilters: { [key]: value } });
};

const dynamicField = (key, defaultValue) => computed({
  get() {
    const value = props.modelValue?.dynamicFilters?.[key];
    if (value === undefined || value === null) return defaultValue;
    return value;
  },
  set(value) {
    emitDynamic(key, value);
  },
});

const nameValue = dynamicField('name', '');
const usernameValue = dynamicField('username', []);
const statusValue = dynamicField('status', []);
const extensionValue = dynamicField('file_ext', []);

const datesValue = computed({
  get() {
    return {
      startDate: props.modelValue?.startDate || '',
      endDate: props.modelValue?.endDate || '',
    };
  },
  set(value) {
    emitUpdate({
      startDate: value?.startDate || '',
      endDate: value?.endDate || '',
    });
  },
});
</script>

<style lang="scss" scoped>
.snapshot-filters {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
